export type DocumentBlockKind =
  | "header"
  | "title"
  | "body"
  | "nonBody"
  | "bibliographyHeading"
  | "bibliographyEntry";

export type DocumentBlockInput = {
  sourceIndex: number;
  start: number;
  end: number;
  text: string;
  isAttributedHeader: boolean;
  isExplicitlyCentered: boolean;
};

export type DocumentBlock = DocumentBlockInput & {
  kind: DocumentBlockKind;
  reasons: string[];
};

export type DocumentBlockAnalysis = {
  blocks: DocumentBlock[];
  bodyBlocks: DocumentBlock[];
  bodyParagraphCount: number;
};

const bibliographyHeadings = [
  "bibliography",
  "works cited",
  "works consulted",
];

const manuallyCenteredTitleMaxCharacters = 120;
const manuallyCenteredTitleMinTabs = 2;
const manuallyCenteredTitleMinSpaces = 8;
const minimumBodyParagraphWords = 5;

const normalizeHeading = (text: string) => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const isLabeledHeader = (text: string) => {
  return /^(name|date|teacher|class|course|assignment)\s*:/i.test(text.trim());
};

const isDate = (text: string) => {
  return /^(?:(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s*|\s+)\d{2,4}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})$/i.test(
    text.trim()
  );
};

const getLeadingWhitespaceCounts = (text: string) => {
  const leadingWhitespace = text.match(/^[\t ]*/)?.[0] ?? "";

  return {
    tabs: [...leadingWhitespace].filter((character) => character === "\t")
      .length,
    spaces: [...leadingWhitespace].filter((character) => character === " ")
      .length,
  };
};

const getWordCount = (text: string) => {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.replace(/\u200B/g, "").length > 0).length;
};

export const snapshotDocumentBlockInputs = (
  paragraphs: any[],
  headerParagraphIndexes: ReadonlySet<number> = new Set<number>()
): DocumentBlockInput[] => {
  return paragraphs.map((paragraph, sourceIndex) => {
    const domNode = paragraph.domNode as HTMLElement;
    const text = domNode.innerText ?? "";

    return {
      sourceIndex,
      start: paragraph.start ?? 0,
      end: paragraph.end ?? paragraph.start ?? 0,
      text,
      isAttributedHeader: headerParagraphIndexes.has(sourceIndex),
      isExplicitlyCentered:
        domNode.classList.contains("ql-align-center") ||
        domNode.style.textAlign === "center" ||
        domNode.getAttribute("align") === "center",
    };
  });
};

export const analyzeDocumentBlocks = (
  inputs: DocumentBlockInput[]
): DocumentBlockAnalysis => {
  const blocks: DocumentBlock[] = [];
  let hasSeenBody = false;
  let hasSeenBibliography = false;

  for (const input of inputs) {
    const text = input.text.trim();
    const normalizedHeading = normalizeHeading(input.text);

    if (bibliographyHeadings.includes(normalizedHeading)) {
      hasSeenBibliography = true;
      blocks.push({
        ...input,
        kind: "bibliographyHeading",
        reasons: ["recognized-bibliography-heading"],
      });
      continue;
    }

    if (hasSeenBibliography) {
      blocks.push({
        ...input,
        kind: "bibliographyEntry",
        reasons: ["follows-bibliography-heading"],
      });
      continue;
    }

    if (input.isAttributedHeader || isLabeledHeader(text) || isDate(text)) {
      const reasons: string[] = [];
      if (input.isAttributedHeader) reasons.push("header-attribute");
      if (isLabeledHeader(text)) reasons.push("labeled-header");
      if (isDate(text)) reasons.push("date-pattern");

      blocks.push({
        ...input,
        kind: "header",
        reasons,
      });
      continue;
    }

    if (!hasSeenBody && input.isExplicitlyCentered) {
      blocks.push({
        ...input,
        kind: "title",
        reasons: ["explicitly-centered-before-body"],
      });
      continue;
    }

    const leadingWhitespace = getLeadingWhitespaceCounts(input.text);
    const isShortTitleCandidate =
      text.length <= manuallyCenteredTitleMaxCharacters;
    const hasManualCentering =
      leadingWhitespace.tabs >= manuallyCenteredTitleMinTabs ||
      leadingWhitespace.spaces >= manuallyCenteredTitleMinSpaces;

    if (!hasSeenBody && isShortTitleCandidate && hasManualCentering) {
      blocks.push({
        ...input,
        kind: "title",
        reasons: [
          leadingWhitespace.tabs >= manuallyCenteredTitleMinTabs
            ? "multiple-leading-tabs-before-body"
            : "large-leading-space-indent-before-body",
          "short-title-candidate",
        ],
      });
      continue;
    }

    if (getWordCount(text) < minimumBodyParagraphWords) {
      blocks.push({
        ...input,
        kind: "nonBody",
        reasons: [
          hasSeenBody
            ? "fewer-than-five-words-after-body-start"
            : "fewer-than-five-words-before-body-start",
        ],
      });
      continue;
    }

    hasSeenBody = true;
    blocks.push({
      ...input,
      kind: "body",
      reasons: [
        blocks.some((block) => block.kind === "body")
          ? "content-after-body-start"
          : "first-body-content",
      ],
    });
  }

  const bodyBlocks = blocks.filter((block) => block.kind === "body");

  return {
    blocks,
    bodyBlocks,
    bodyParagraphCount: bodyBlocks.length,
  };
};

export const getBodyParagraphCountMessage = (
  currentCount: number,
  expectedCount: number
) => {
  if (currentCount < expectedCount) return "You are missing paragraphs.";
  if (currentCount > expectedCount) return "You have too many paragraphs.";
  return "";
};
